import EditIcon from "@mui/icons-material/Edit";
import ActionButton from "Common/Components/Buttons/ActionButton";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import ListingPageContainer from "Common/Components/PageContainer/ListingPageContainer";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import usePermission from "hooks/usePermission";
import {
  useEffect,
  useState
} from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  exportGstCSV,
  fetchGsts as OnfetchGst,
  updateStatusInGst
} from "slices/Masters/gst/thunk";

const Gst = () => {
  document.title = "Gst List";
  const dispatch = useDispatch<any>();

  const selectGst = createSelector(
    (state: any) => state.GST,
    (gst) => ({
      Gsts: gst.gstList,
      rowCount: gst.rowCount,
      loading: gst.loading,
      error: gst.error,
    })
  );

  const { Gsts, rowCount, loading, error } = useSelector(selectGst);

  const { canView, canCreate, canUpdate, canDelete } = usePermission(
    app_modules?.master?.app_name,
    app_modules?.master?.modules?.gst
  );

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [data, setData] = useState(Gsts || []);
  const [totalRecords, setTotalRecords] = useState(rowCount);
  const [tableInstance, setTableInstance] = useState<any>();
  // State for edit functionality
  const [editUnit, setEditUnit] = useState<boolean>(false);
  // const [columnFilters, setColumnFilters] = useState<any>();
  const [edit, setEdit] = useState<any>();
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [searchInput, setSearchInput] = useState("");
  // const [search, setSearch] = useState("");

  // const [filterValue, setFilterValue] = useState<any[]>([]);

  const tableState = usePreservedTableState({
    key: TableKeys.MASTER.GST,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const [isStatusUpdateLoading, setIsStatusUpdateLoading] =
    useState<boolean>(false);

  useEffect(() => {
    dispatch(
      OnfetchGst({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
  }, [dispatch, pageIndex, pageSize, sorting, search, filterValue]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  // Handle edit action
  const handleEdit = (item: any) => {
    setEditUnit(!editUnit);
    setEdit({
      id: item.id,
      UnitName: item.Unit_name,
      state: item.state_name,
      status: item.is_active,
      createdBy: item.created_by,
      updatedBy: item.updated_by,
      createDate: item.created_at,
      updatedDate: item.updated_at,
    });
  };
  const handleStatusToggle = async (pkg: any) => {
    try {
      const updatedStatus = !pkg.is_active;
      const updatedUnit = {
        id: pkg.gst_id,
        is_active: updatedStatus,
      };
      setIsStatusUpdateLoading(true);

      // Properly await the dispatch call
      await dispatch(updateStatusInGst(updatedUnit))
        .unwrap()
        .then(() => {
          toast.success(
            `GST ${updatedStatus ? "activated" : "deactivated"} successfully`
          );
          // Refetch data after successful update
          dispatch(
            OnfetchGst({
              page: pageIndex + 1,
              pageSize,
              sort: sortField,
              order: sortField ? sortDirection : "",
              search,
              filters: filterValue,
            })
          );
        });
    } catch (err: any) {
      toast.error("Failed to Update status!");
      console.error("GST status update error:", err);
    } finally {
      setIsStatusUpdateLoading(false);
    }
  };

  const columns = [
    {
      header: "Sr. No.",
      accessorKey: "sr_no",
      enableColumnFilter: false,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl: API_ENDPOINT.MASTERS.GST.FILTER_DROPDOWN("sr_no"),
    },
    {
      header: "GST %",
      accessorKey: "gst_percentage",
      enableSorting: true,
      enableColumnFilter: true,
      filterType: "dropdown" as const,
      filterFetchUrl:
        API_ENDPOINT.MASTERS.GST.FILTER_DROPDOWN("gst_percentage"),
      // cell: ({ getValue }: any) => `${String(getValue())}%`,
    },

    {
      header: "Status",
      accessorKey: "is_active",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => {
        const gst = row.original;

        return (
          <div
            style={{ fontSize: "1.5rem", cursor: "pointer" }}
            onClick={() => handleStatusToggle(gst)}
          >
            <i
              className={`bi ${
                gst.is_active
                  ? "bi-toggle-on text-success"
                  : "bi-toggle-off text-danger"
              }`}
            />
          </div>
        );
      },
    },

    {
      header: "Created By/Date",
      accessorKey: "created_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      cell: ({ row }: { row: any }) => (
        <NameWithDate
          name={row.original.created_by_user?.username}
          date={row.original.created_at}
        />
      ),
    },
    {
      header: "Updated By/Date",
      accessorKey: "updated_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      cell: ({ row }: { row: any }) => (
        <NameWithDate
          name={row.original.updated_by_user?.username}
          date={row.original.updated_at}
        />
      ),
    },
    // ...(gst_permissions?.permissions?.Update === true
    //   ? [
    // Filter menu items to check if any will be visible
    // This ensures we only show the Action column if there are visible menu items
    ...(canUpdate
      ? [
          {
            header: "Action",
            accessorKey: "action",
            enableColumnFilter: false,
            enableSorting: false,
            cell: ({ row }: { row: any }) => {
              const menuItems = [
                {
                  icon: EditIcon,
                  label: "Edit",
                  permission: canUpdate,
                  link: `/master/edit-gst/${row.original.gst_id}`,
                  color: "primary",
                },
              ].filter((item) => item.permission === true);
              return (
                <DynamicMenuItem row={row.original} menuItems={menuItems} />
              );
            },
          },
        ]
      : []),
  ];
  function handleChange(e: any): void {
    setSearchInput(e.target.value);
  }

  return (
    <ListingPageContainer>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card className="shadow-none">
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">GST</h6>
                </Col>
                <Row className="align-items-center justify-content-between mt-1 px-0 g-3 flex-wrap">
                  {/* Search Box */}
                  <SearchBox
                    value={searchInput}
                    onChange={(e: any) => setSearchInput(e.target.value)}
                    placeholder="Search..."
                  />

                  {/* Column Visibility + Actions */}
                  <Col xs={12} md={12} lg={8} xl={6} className="">
                    <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 w-100">
                      {/* Left side: Column Visibility */}
                      <div className="">
                        {tableInstance && (
                          <ColumnVisibilityDropdown
                            columns={tableInstance.getAllLeafColumns()}
                            excludedColumns={["action", "sr_no"]}
                          />
                        )}
                      </div>

                      <div className="hstack gap-2">
                        {canView && (
                          <ExportButton
                            onExport={async () => {
                              return await exportGstCSV({
                                search,
                                filters: filterValue,
                              });
                            }}
                          />
                        )}
                      </div>
                      <div>
                        {canCreate && (
                          <ActionButton
                            label="Add GST"
                            icon="bi bi-plus-circle"
                            navigateLink="/master/add-gst"
                            variant="primary"
                          />
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper
                loading={loading || isStatusUpdateLoading}
                error={error}
              >
                <MasterTableContainer
                  columns={columns}
                  data={Gsts || []}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  isBordered={false}
                  isPagination={true}
                  manualSorting={true}
                  colValue={setFilterValue}
                  onTableReady={setTableInstance}
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default Gst;
